import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ErpLink } from "../components/ErpLink";
import { Card, ErrorBox, Skeleton } from "../components/ui";
import UploadDocument from "./compliance/Upload";
import {
  complianceAuditPack, useComplianceAccessLog, useComplianceDocuments, useComplianceOverview, viewComplianceDocument,
  type ComplianceDoc,
} from "../lib/api";

type Props = { company?: string };

const STATUS_PILL: Record<string, string> = {
  Expired: "bg-red-50 text-status-red border-status-red/40",
  Expiring: "bg-amber-50 text-status-amber border-status-amber/40",
  Valid: "bg-green-50 text-status-green border-status-green/40",
  "On File": "bg-brand-grey-light text-brand-charcoal border-brand-grey-light",
};

/** A document's detail (marks it viewed — HR-15 access log) plus who else has viewed it, in one popover. */
function DocumentPanel({ doc }: { doc: ComplianceDoc }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof viewComplianceDocument>> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const log = useComplianceAccessLog(doc.name, open);

  const openPanel = async () => {
    setOpen(true); setLoading(true); setError(null);
    try { setDetail(await viewComplianceDocument(doc.name)); void log.refetch(); } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setLoading(false); }
  };

  if (!open) return <button onClick={() => void openPanel()} className="text-xs text-brand-primary hover:underline" data-compliance-view={doc.name}>{t("compliance.view")}</button>;

  return (
    <div className="w-72 rounded border border-brand-grey-light bg-white p-2 text-left shadow-sm" data-compliance-panel={doc.name}>
      {loading ? <Skeleton className="h-16" /> : error ? <ErrorBox error={error} /> : detail && (
        <div className="space-y-1 text-xs">
          <div className="font-semibold">{detail.document_type}</div>
          <div className="text-brand-grey">{detail.reference_no || "–"} · {detail.issuing_authority || "–"}</div>
          {detail.document && <a href={detail.document} target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:underline">{t("compliance.openFile")}</a>}
          {detail.notes && <div className="text-brand-grey">{detail.notes}</div>}
        </div>
      )}
      <div className="mt-2 border-t border-brand-grey-light pt-2">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-brand-grey">{t("compliance.accessLog")}</div>
        {log.isLoading ? <Skeleton className="mt-1 h-6" /> : (log.data ?? []).length === 0 ? (
          <p className="mt-1 text-[11px] text-brand-grey">{t("compliance.noViews")}</p>
        ) : (
          <ul className="mt-1 space-y-0.5 text-[11px]">
            {(log.data ?? []).slice(0, 8).map((v, i) => <li key={i} className="flex justify-between gap-2"><span>{v.viewed_by}</span><span className="text-brand-grey">{v.viewed_on.slice(0, 16).replace("T", " ")}</span></li>)}
          </ul>
        )}
      </div>
      <div className="mt-2 flex items-center justify-between">
        <ErpLink>{t("compliance.openInErp")}</ErpLink>
        <button onClick={() => setOpen(false)} className="text-xs text-brand-grey hover:text-brand-charcoal">{t("common.close")}</button>
      </div>
    </div>
  );
}

/** Compliance repository (WP-11.3, HR-12…15): dashboard over document status and missing mandatory document
 * types, per-document access log, and an audit-pack PDF. Creating or editing a document (including the file
 * attach) happens on the ERPNext desk form — this page reads, logs and packages what is already there. */
export default function Compliance({ company }: Props) {
  const { t } = useTranslation();
  const overview = useComplianceOverview(company, true);
  const [documentType, setDocumentType] = useState("");
  const [status, setStatus] = useState("");
  const docs = useComplianceDocuments({ company, document_type: documentType || undefined, status: status || undefined }, true);
  const [packBusy, setPackBusy] = useState(false);
  const [packError, setPackError] = useState<string | null>(null);

  const downloadPack = async () => {
    setPackBusy(true); setPackError(null);
    try {
      const r = await complianceAuditPack(company);
      const bytes = Uint8Array.from(atob(r.content_base64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: r.mime }));
      const a = document.createElement("a");
      a.href = url; a.download = r.file_name; a.rel = "noopener"; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { setPackError(e instanceof Error ? e.message : String(e)); } finally { setPackBusy(false); }
  };

  const data = overview.data;
  const documentTypes = ["ID Card", "Passport", "Work Permit", "Residence Permit", "Employment Contract", "Background Check", "Medical Fitness Certificate",
    "Non-Disclosure Agreement", "Diploma or Degree", "Driving License", "Bank Details Form", "Reference Letter", "Other"];

  return (
    <div className="mt-4 space-y-4" data-compliance-page>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-brand-grey">{t("compliance.hint")}</p>
        <button disabled={packBusy} onClick={() => void downloadPack()} className="rounded border border-brand-primary px-3 py-1.5 text-sm font-semibold text-brand-primary hover:bg-blue-50 disabled:opacity-50" data-compliance-audit-pack>
          {packBusy ? t("compliance.packing") : t("compliance.auditPack")}
        </button>
      </div>
      {packError && <ErrorBox error={packError} />}
      <UploadDocument />

      {overview.isError && <ErrorBox error={overview.error} onRetry={() => void overview.refetch()} />}
      {overview.isLoading ? <div className="grid grid-cols-2 gap-3 md:grid-cols-5">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-20" />)}</div> : data && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <Card className="px-4 py-3"><div className="font-heading text-2xl font-semibold">{data.employees_total}</div><div className="text-[11px] uppercase tracking-wide text-brand-grey">{t("compliance.employees")}</div></Card>
            {(["Valid", "Expiring", "Expired", "On File"] as const).map((s) => (
              <Card key={s} className="px-4 py-3">
                <div className={`font-heading text-2xl font-semibold ${s === "Expired" ? "text-status-red" : s === "Expiring" ? "text-status-amber" : s === "Valid" ? "text-status-green" : ""}`}>{data.counts[s] ?? 0}</div>
                <div className="text-[11px] uppercase tracking-wide text-brand-grey">{t(`compliance.status.${s}`)}</div>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card className="overflow-hidden">
              <div className="flex items-center justify-between border-b border-brand-grey-light px-4 py-3">
                <h2 className="font-heading text-base font-semibold">{t("compliance.attention")}</h2>
                <span className="text-xs text-brand-grey">{t("compliance.attentionHint", { n: data.warn_days })}</span>
              </div>
              {data.attention.length === 0 ? <div className="p-6 text-center text-sm text-brand-grey">{t("compliance.noAttention")}</div> : (
                <table className="w-full text-sm">
                  <tbody>
                    {data.attention.map((d) => (
                      <tr key={d.name} className="border-t border-brand-grey-light">
                        <td className="px-4 py-2"><span className={`rounded border px-2 py-0.5 text-[11px] font-semibold ${STATUS_PILL[d.status]}`}>{t(`compliance.status.${d.status}`)}</span></td>
                        <td className="px-4 py-2"><div className="font-semibold">{d.employee_name}</div><div className="text-[11px] text-brand-grey">{d.company}</div></td>
                        <td className="px-4 py-2 text-xs">{d.document_type}</td>
                        <td className="px-4 py-2 text-right text-xs">{d.expires_on}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>

            <Card className="overflow-hidden">
              <div className="flex items-center justify-between border-b border-brand-grey-light px-4 py-3">
                <h2 className="font-heading text-base font-semibold">{t("compliance.missing")}</h2>
                <span className="text-xs text-brand-grey">{t("compliance.missingHint", { types: data.mandatory_document_types.join(", ") })}</span>
              </div>
              {data.missing.length === 0 ? <div className="p-6 text-center text-sm text-brand-grey">{t("compliance.noMissing")}</div> : (
                <table className="w-full text-sm">
                  <tbody>
                    {data.missing.map((m) => (
                      <tr key={m.employee} className="border-t border-brand-grey-light">
                        <td className="px-4 py-2"><div className="font-semibold">{m.employee_name}</div><div className="text-[11px] text-brand-grey">{m.company} · {m.designation}</div></td>
                        <td className="px-4 py-2 text-right text-xs text-status-red">{m.missing.join(", ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </div>
        </>
      )}

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-brand-grey-light px-4 py-3">
          <h2 className="font-heading text-base font-semibold">{t("compliance.repository")}</h2>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <select value={documentType} onChange={(e) => setDocumentType(e.target.value)} className="rounded border border-brand-grey-light px-1 py-1">
              <option value="">{t("common.all")}</option>
              {documentTypes.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded border border-brand-grey-light px-1 py-1">
              <option value="">{t("common.all")}</option>
              {(["Valid", "Expiring", "Expired", "On File"] as const).map((s) => <option key={s} value={s}>{t(`compliance.status.${s}`)}</option>)}
            </select>
          </div>
        </div>
        {docs.isError && <div className="p-4"><ErrorBox error={docs.error} onRetry={() => void docs.refetch()} /></div>}
        {docs.isLoading ? <div className="space-y-2 p-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-8" />)}</div> : (docs.data ?? []).length === 0 ? (
          <div className="p-8 text-center text-sm text-brand-grey">{t("compliance.noDocuments")}</div>
        ) : (
          <div className="max-h-[28rem] overflow-y-auto overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
                <tr><th className="px-4 py-2">{t("compliance.employee")}</th><th className="px-4 py-2">{t("compliance.type")}</th><th className="px-4 py-2">{t("compliance.statusLabel")}</th><th className="px-4 py-2 text-right">{t("compliance.expires")}</th><th className="px-4 py-2 text-right">{t("compliance.view")}</th></tr>
              </thead>
              <tbody>
                {(docs.data ?? []).map((d) => (
                  <tr key={d.name} className="border-t border-brand-grey-light hover:bg-[#F6F8FA]" data-compliance-row={d.name}>
                    <td className="px-4 py-2"><div className="font-semibold">{d.employee_name}</div><div className="text-[11px] text-brand-grey">{d.company}</div></td>
                    <td className="px-4 py-2 text-xs">{d.document_type}</td>
                    <td className="px-4 py-2"><span className={`rounded border px-2 py-0.5 text-[11px] font-semibold ${STATUS_PILL[d.status]}`}>{t(`compliance.status.${d.status}`)}</span></td>
                    <td className="px-4 py-2 text-right text-xs">{d.expires_on || "–"}</td>
                    <td className="px-4 py-2 text-right"><DocumentPanel doc={d} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
